import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  XCircle, 
  Loader2,
  ArrowRight,
  Shield
} from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const pollingRef = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !pollingRef.current) {
      pollingRef.current = true;
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Impossible de vérifier le statut du paiement');
      return;
    }

    try {
      const response = await paymentsApi.getStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        await refreshUser();
        toast.success('Paiement réussi ! Bienvenue dans le plan Solo.');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('La session de paiement a expiré');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center shadow-sm">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-sky-500 rounded flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-[Barlow]">Drone Maps</span>
          </div>

          {status === 'loading' && (
            <>
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Barlow] mb-2">
                Vérification en cours...
              </h1>
              <p className="text-slate-600">
                Nous vérifions votre paiement. Veuillez patienter quelques instants.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Barlow] mb-2">
                Paiement réussi !
              </h1>
              <p className="text-slate-600 mb-6">
                Votre abonnement Solo est maintenant actif. Profitez de toutes les fonctionnalités premium.
              </p>
              
              {paymentInfo && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-slate-500">Montant payé</p>
                  <p className="text-lg font-bold text-slate-900">
                    {(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                  </p>
                </div>
              )}

              <Link to="/dashboard">
                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="go-to-dashboard-btn">
                  Accéder au tableau de bord
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Barlow] mb-2">
                Erreur de paiement
              </h1>
              <p className="text-slate-600 mb-6">
                Nous n'avons pas pu confirmer votre paiement. Veuillez réessayer ou contacter le support.
              </p>
              <div className="space-y-3">
                <Link to="/subscription">
                  <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                    Réessayer
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">
                    Retour au tableau de bord
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
